import { CreateLoaderTable } from "@/components/Loaders";
import { Main } from "@/components/Main";
import { useCallback, useEffect, useRef, useState } from "react";
import { ApiDataProps } from "../Tenants";
import { TypePageActions } from "../Plans";
import { AddBtn } from "@/App/Products/Add";
import { TableData } from "@/App/Products/Table";
import { getProductsData } from "@/Api/products";
import { getSelectProductGroups } from "@/Api/productGroups";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { Box } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { cn } from "@/lib/utils";
import { ProductsFiltersCard } from "@/components/ProductsFilters";
import {
  LotteryOption,
  ProductFilterLimits,
  ProductsFilters,
  clampFiltersToLimits,
} from "@/App/Products/filters";

export type TableDataProps = {
  id: number;
  uuid: string;
  lottery_id: number;
  name: string;
  game_quantity: number;
  created_by: {
    id: number;
    name: string;
  };
  dez_quantity: number;
  quota_quantity: number;
  total_without_tariff: number;
  total_with_tariff: number;
  game_percentage: number;
  tariff: number;
  sale_price: number;
  price_without_tariff: number;
  commission_by_quote: number;
  image: string | null;
  is_active: number;
  product_type_id: number;
  tenant_id: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  product_type: {
    id: number;
    name: string;
  };
  lottery: {
    id: number;
    name: string;
  };
};

export const PageList = () => {
  const [listData, setListData] = useState({} as ApiDataProps<TableDataProps>);
  const [state, setState] = useState("loading");
  const [filters, setFilters] = useState<ProductsFilters>({});
  const [page, setPage] = useState(1);
  const [lotteries, setLotteries] = useState<LotteryOption[]>([]);
  // null enquanto a primeira resposta da listagem não chegou.
  const [limits, setLimits] = useState<ProductFilterLimits | null>(null);
  const { hasPermission } = usePermissions();

  // Evita que uma resposta antiga sobrescreva uma mais recente.
  const requestId = useRef(0);

  // Opções do multiselect de loteria.
  useEffect(() => {
    (async () => {
      const { data } = await getSelectProductGroups();
      setLotteries(data?.response || []);
    })();
  }, []);

  // Listagem: sempre server-side (filtros + paginação vão para a API).
  // A mesma resposta traz `limits`, os limites reais de cada coluna numérica.
  useEffect(() => {
    const currentRequest = ++requestId.current;

    (async () => {
      setState("loading");
      const { data } = await getProductsData(page, filters);

      if (currentRequest !== requestId.current) return;

      const response = data?.response;
      const nextLimits = (response?.limits ?? null) as ProductFilterLimits | null;

      setListData(response);
      // `?? {}` encerra o skeleton mesmo se a resposta vier sem `limits`;
      // nesse caso cada coluna cai no estado "sem valores disponíveis" em vez
      // de exibir um limite inventado.
      setLimits(nextLimits ?? {});
      setState("production");

      // Se os limites mudaram (troca de loteria/nome), reajusta os intervalos
      // já selecionados. Retorna o mesmo objeto quando nada muda, então isso
      // não entra em loop de requisições.
      if (nextLimits) {
        const clamped = clampFiltersToLimits(filters, nextLimits);
        if (clamped !== filters) {
          setFilters(clamped);
          setPage(1);
        }
      }
    })();
  }, [page, filters]);

  const handleFiltersChange = useCallback((nextFilters: ProductsFilters) => {
    setFilters(nextFilters);
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
    setPage(1);
  }, []);

  const isInitialLoading = state === "loading" && !listData?.data;

  const pageActions: TypePageActions = {
    next: () => {
      if (listData?.current_page < listData?.last_page) {
        setPage(listData.current_page + 1);
      }
    },
    previous: () => {
      if (listData?.current_page > 1) {
        setPage(listData.current_page - 1);
      }
    },
    lastPage: () => {
      if (listData?.current_page !== listData?.last_page) {
        setPage(listData?.last_page);
      }
    },
  };

  return (
    <Main>
      <If condition={hasPermission("view-products-list")}>
        <PageHeader
          icon={Box}
          title="Produtos"
          description="Gerencie os produtos disponiveis"
        >
          <If condition={hasPermission("create-product")}>
            <AddBtn updateFront={setListData} />
          </If>
        </PageHeader>
        <div className="flex flex-col gap-4">
          <ProductsFiltersCard
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onClear={clearFilters}
            lotteries={lotteries}
            limits={limits ?? {}}
            loadingLimits={limits === null}
          />
          {isInitialLoading ? (
            <CreateLoaderTable
              cols={4}
              lines={6}
            />
          ) : (
            // A tabela permanece montada durante as buscas para que o card de
            // filtros não perca o foco/estado a cada requisição.
            <div
              className={cn(
                "transition-opacity",
                state === "loading" && "opacity-60"
              )}
              aria-busy={state === "loading"}
            >
              <TableData
                dataApi={listData}
                pageActions={pageActions}
                updateFront={setListData}
              />
            </div>
          )}
        </div>
      </If>
    </Main>
  );
};
